import { PhoneCall, Gamepad2, BrainCircuit, ShieldCheck, HeartPulse, ArrowRight, Trophy, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: Trophy,
    title: 'enGAMEment Loops',
    link: '/enGAMEment',
    description: 'Initiate and maintain daily brand engagement with a digital marketing loop that combines AI-driven story videos, light games, and real rewards.',
    features: ['AI Story Videos', 'Light Games', 'Real Rewards', 'Sponsor Integration']
  },
  {
    icon: Gamepad2,
    title: 'Gamified Professional Development',
    link: '/pd',
    description: 'Strengthen company culture and retain employees through immersive team-building experiences that develop leadership and collaboration.',
    features: ['On-Site Experiences', 'Leadership Development', 'Culture Building', 'Team Analytics']
  },
  {
    icon: BrainCircuit,
    title: 'AI Workforce Intelligence',
    link: '/workforce',
    description: 'Anticipate staffing challenges, reduce turnover, and protect margins with AI-powered workforce analytics and actionable insights.',
    features: ['Turnover Prediction', 'Staffing Forecasting', 'Budget Optimization', 'Executive Dashboards']
  },
  {
    icon: PhoneCall,
    title: 'AI Receptionist',
    link: '/receptionist',
    description: 'A 24/7 AI employee that answers every call, recovers missed calls in under 60 seconds, books appointments, and reactivates inactive clients — so you never lose revenue to a missed ring again.',
    features: ['24/7 Voice AI', 'Missed-Call SMS Recovery', 'Appointment Booking', 'Live Revenue Dashboard']
  },
  {
    icon: Bot,
    title: 'AI Service Robots',
    link: '/robotics',
    description: 'Autonomous cleaning and delivery robots that cover floors, haul supplies, and run overnight — designed, deployed, and managed for you.',
    features: ['Autonomous Cleaning Robots', 'Hotel & Building Delivery', 'Healthcare & Senior Care Transport', 'Fleet Dashboard & Support']
  },
  {
    icon: ShieldCheck,
    title: 'AI Provenance & Governance',
    link: '/aipolice',
    description: 'Cryptographically prove where AI content came from with immutable, on-chain provenance for AI-generated output.',
    features: ['Agent-Signed Receipts', 'On-Chain Registry', 'Verification API', 'Enterprise Governance']
  },
  {
    icon: HeartPulse,
    title: 'Healthcare Workforce Intelligence',
    link: '/healthcare',
    description: 'Predictive workforce intelligence for hospitals, clinics, and health systems — see where nurse turnover risk is building and what it will cost before it hits operations.',
    features: ['Turnover Risk Prediction', 'Financial Exposure Modeling', 'At-Risk Unit Alerts', 'No PHI Required']
  },
];
const ServicesOverview = () => {
  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            How We Help Your Business Thrive
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From boosting profitability to building a culture that retains top talent, 
            our technology-driven consulting services deliver measurable results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className="relative group hover:shadow-strong transition-all duration-300 border-border hover:border-primary/20">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 p-3 rounded-full bg-gradient-primary w-fit">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription className="text-muted-foreground mb-4 leading-relaxed">
                    {service.description}
                  </CardDescription>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center justify-center">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" size="sm" className="mt-6" asChild>
                    <Link to={service.link}>
                      Explore {service.title} <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center">
          <Button variant="professional" size="lg" asChild>
            <Link to="/services">
              Explore All Services
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;
